import clsx from 'clsx';
import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import type { ProductAlternative } from '../../../@sprinx/knihovka-types';
import ProductDetailLink from '../../../components/ProductDetailLink';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import ProductDetailAlternativeRow from './ProductDetailAlternativeRow';

export interface ProductDetailAlternativesProps extends StandardDivProps<ProductDetailAlternativesClassKey> {
  isProductDetail?: boolean;
  limit?: number;
  productAlternatives: ProductAlternative[] | undefined;
  productLink?: string;
  sectionName: string;
}

export type ProductDetailAlternativesClassKey = 'root' | 'itemRow' | 'name' | 'list' | 'higlight' | 'bold' | 'headline';

const themeSettings = { name: 'ProductDetailAlternatives' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<ProductDetailAlternativesClassKey, {}>({
      root: {},
      itemRow: {},
      name: {
        '& > h1': { fontSize: 18 },
      },
      higlight: {
        margin: theme.spacing(1, 0),
        color: 'red',
      },
      list: {
        margin: theme.spacing(1, 0),
      },
      bold: {
        fontWeight: 500,
      },
      headline: {
        ...theme.typography.h4,
        padding: theme.spacing(3, 0),
      },
    }),
  themeSettings,
);

const ProductDetailAlternatives = React.forwardRef<HTMLDivElement, ProductDetailAlternativesProps>(
  (
    {
      className,
      classes: pClasses,
      sectionName,
      productAlternatives,
      isProductDetail = false,
      limit = null,
      productLink = '',
    },
    ref,
  ) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();

    const isValidLimit =
      isProductDetail === false &&
      limit &&
      limit > 0 &&
      productLink &&
      productAlternatives &&
      productAlternatives.length > limit;

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        {isProductDetail ? (
          <div className={classes.headline}>{sectionName}</div>
        ) : (
          <div className={classes.bold}>{sectionName}</div>
        )}
        {productAlternatives && productAlternatives.length ? (
          <ul className={classes.list}>
            {(isValidLimit ? productAlternatives.slice(0, limit) : productAlternatives).map((v, idx) => (
              <li key={idx}>
                <ProductDetailAlternativeRow product={v} className={classes.itemRow} />
              </li>
            ))}
            {isValidLimit && (
              <li>
                <ProductDetailLink className={classes.name} to={productLink || ''} variant='inherit'>
                  <span>{t('cataloguePage/moreOnProductDetail')}</span>
                </ProductDetailLink>
              </li>
            )}
          </ul>
        ) : (
          <div className={classes.higlight}>{t('productDetailPage/alternativesNotFound')}</div>
        )}
      </div>
    );
  },
);

ProductDetailAlternatives.displayName = 'ProductDetailAlternatives';

export default withThemeProps(themeSettings)(ProductDetailAlternatives);
